// A seed's remixes and version history, both rendered as the clickable lineage
// tree. Two tabs: Remixes (other producers' takes) and Versions (the same
// producer's takes) — both always visible, both navigable.

import { useMemo, useState } from "react";
import { Link } from "react-router";
import { GitBranch, Loader2, Pause, Play } from "lucide-react";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { relativeTime } from "@web-shared/lib/format";
import { profilePath, seedPath } from "@/lib/ctas";
import { readRemixes, type RemixItem } from "@/lib/lineage";
import { oneOf, usePref } from "@web-shared/hooks/usePref";
import { cn } from "@web-shared/lib/utils";
import { usePlayer, useTrackStatus, type PlayerTrack } from "../player";
import { Avatar } from "./Avatar";
import { RemixTree, useSeedTree } from "./RemixTree";

/** How many remixers a page shows before "show all". */
const PAGE = 8;

const VIEWS = ["remixes", "versions"] as const;
type View = (typeof VIEWS)[number];
const isView = oneOf(VIEWS);

/** A row's own track. Lives here, not in lib/: lib must not know the player. */
function remixTrack(item: RemixItem): PlayerTrack | null {
  if (!item.audioUrl) return null;
  return {
    id: item.id,
    title: item.title,
    url: item.audioUrl,
    producer: {
      handle: item.producer.handle ?? item.producer.id,
      username: item.producer.username,
    },
    href: seedPath(item.id),
    countsFor: item.id,
  };
}

function RemixRow({ item, queue }: { item: RemixItem; queue: PlayerTrack[] }) {
  const player = usePlayer();
  const status = useTrackStatus(item.id);
  const track = remixTrack(item);
  const handle = item.producer.handle ?? item.producer.id;
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <button
        type="button"
        disabled={!track}
        onClick={() => track && player.toggle(track, queue)}
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-full border transition-colors",
          track
            ? "border-strong text-foreground hover:border-primary hover:text-primary"
            : "border-hairline text-muted-foreground/40",
        )}
        aria-label={
          track
            ? `${status === "playing" ? "Pause" : "Play"} ${item.title} by ${item.producer.username}`
            : `${item.title} has no audio to play`
        }
      >
        {status === "loading" ? (
          <Loader2 className="size-4 animate-spin" />
        ) : status === "playing" ? (
          <Pause className="size-4" />
        ) : (
          <Play className="size-4" />
        )}
      </button>
      <Link to={profilePath(handle)} className="shrink-0">
        <Avatar name={item.producer.username} url={item.producer.avatarUrl} className="size-8" />
      </Link>
      <Link to={seedPath(item.id)} className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5 text-sm">
          <span className="truncate font-medium">{item.producer.username}</span>
          <span className="shrink-0 font-mono text-[10px] text-muted-foreground">v{item.version}</span>
          {item.takeCount > 1 && (
            <span className="shrink-0 text-[10px] text-muted-foreground">· {item.takeCount} takes</span>
          )}
          {item.branchStart > 1 && (
            <span
              className="shrink-0 text-[10px] text-muted-foreground"
              title="They built on one of the remixes, not on this seed directly"
            >
              · via a remix
            </span>
          )}
          <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
            {relativeTime(item.createdAt)}
          </span>
        </div>
        {item.note && (
          <p className="line-clamp-2 text-xs text-muted-foreground">&ldquo;{item.note}&rdquo;</p>
        )}
      </Link>
    </li>
  );
}

/**
 * The panel. `inline` drops the section chrome for the mobile tab, exactly as
 * `RemixTree` already did.
 */
export function Remixes({ seedId, inline }: { seedId: string; inline?: boolean }) {
  const tree = useSeedTree(seedId);
  const [view, setView] = usePref<View>("seedbed.remixes.view", "remixes", isView);
  const [shown, setShown] = useState(PAGE);
  const reading = useMemo(() => readRemixes(tree.data, { limit: shown }), [tree.data, shown]);
  const queue = useMemo(
    () => reading.items.map(remixTrack).filter((t): t is PlayerTrack => t !== null),
    [reading.items],
  );
  const versionCount = useMemo(() => {
    if (!tree.data) return 0;
    const focus = tree.data.nodes.find((n) => n.id === tree.data!.focusId);
    if (!focus) return 0;
    return tree.data.nodes.filter((n) => n.id !== focus.id && n.producer.id === focus.producer.id).length;
  }, [tree.data]);

  if (tree.isPending) return <Skeleton className={inline ? "h-28 w-full" : "h-28 w-full rounded-xl"} />;

  const nodeCount = tree.data?.nodes.length ?? 0;

  const body = view === "versions" ? (
    nodeCount < 2 ? (
      <p className="px-4 py-6 text-center text-sm text-muted-foreground">
        No other versions yet.
      </p>
    ) : (
      <RemixTree seedId={seedId} inline />
    )
  ) : reading.total === 0 ? (
    <p className="px-4 py-6 text-center text-sm text-muted-foreground">
      No remixes yet. Be the first.
    </p>
  ) : (
    <>
      <ul className="divide-y divide-hairline">
        {reading.items.map((item) => (
          <RemixRow key={item.id} item={item} queue={queue} />
        ))}
      </ul>
      {reading.items.length < reading.total && (
        <button
          type="button"
          onClick={() => setShown((n) => n + PAGE)}
          className="w-full px-4 py-2 text-center text-xs text-primary hover:underline"
        >
          Show all {reading.total}
        </button>
      )}
      {tree.data?.truncated && (
        <p className="px-4 py-2 text-center text-[11px] text-muted-foreground">
          This tree is bigger than we can draw here.
        </p>
      )}
    </>
  );

  const header = (
    <div className={cn("flex items-center gap-2", inline ? "pb-2" : "border-b border-border px-4 py-2.5")}>
      <h2 className="flex items-center gap-1.5 text-sm font-medium">
        <GitBranch className="size-3.5 text-primary" aria-hidden />
        {view === "versions"
          ? <>Versions <span className="text-muted-foreground">· {versionCount}</span></>
          : <>Remixes <span className="text-muted-foreground">· {reading.total}</span></>}
      </h2>
      <div className="ml-auto flex gap-0.5 rounded-lg border border-strong bg-raised p-0.5 text-[11px] font-semibold">
        {VIEWS.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            aria-pressed={view === v}
            aria-label={
              v === "remixes" ? "Show remixes by others"
                : "Show the version tree"
            }
            className={cn(
              "rounded-md px-2 py-0.5 capitalize transition-colors",
              view === v ? "bg-[#262630] text-white" : "text-muted-foreground",
            )}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );

  if (inline) {
    return (
      <div>
        {header}
        {body}
      </div>
    );
  }
  // `id="tree"` lives here now: the feed card's remix Stat deep-links to it,
  // and it used to be on RemixTree, which is no longer always rendered.
  return (
    <section id="tree" className="rounded-xl border border-border bg-card">
      {header}
      {body}
    </section>
  );
}
